import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import propTypes from 'prop-types';
import CustomDialog from '@/components/common/CustomDialog';
import Loader from '@/components/common/Loader';
import RelationNames from '@/constants/constituents/RelationNames';
import LoaderSizes from '@/constants/ui/LoaderSizes';
import { API_DATE_FORMAT } from '@/lib/constants/common';
import useConstituent from '@/lib/hooks/swr/useConstituent';
import translate from '@/lib/intl/translate';
import { DonorRelationType, DonorType } from '@/propTypes';
import HouseholdRelationshipForm, {
  HouseholdRelationLabelOptions,
  RelationStatusOptions,
} from './HouseholdRelationshipForm';
import JobRelationshipForm from './JobRelationshipForm';

const getDefaultValues = (relation) => {
  const isEmployment = [
    RelationNames.EMPLOYEES,
    RelationNames.EMPLOYERS,
  ].includes(relation.relationName);

  const relationLabel = isEmployment
    ? relation.relationLabel
    : HouseholdRelationLabelOptions.find(
        ({ value }) => value === relation.relationLabel,
      );

  const relationStatus = RelationStatusOptions.find(
    ({ value }) => value === relation.relationStatus,
  );

  const relationshipStartDate = relation.relationStartDateString
    ? parseISO(relation.relationStartDateString)
    : null;
  const relationshipEndDate = relation.relationEndDateString
    ? parseISO(relation.relationEndDateString)
    : null;

  return {
    relationLabel,
    relationStatus,
    relationshipStartDate,
    relationshipEndDate,
  };
};

function EditRelationDialog({ relation, parent, onClose, open }) {
  const [isBusy, setIsBusy] = React.useState(false);
  const [apiError, setApiError] = React.useState(null);
  const { updateRelationship } = useConstituent(parent.constituentId);
  const { data: constituent } = useConstituent(relation.relatedId);

  const isEmployment = [
    RelationNames.EMPLOYEES,
    RelationNames.EMPLOYERS,
  ].includes(relation.relationName);

  const formMethods = useForm({
    defaultValues: getDefaultValues(relation),
  });
  const { control, handleSubmit, reset } = formMethods;

  React.useEffect(() => {
    reset(getDefaultValues(relation));
  }, [relation, reset]);

  const submit = async (formData) => {
    const relationStartDateString =
      formData.relationshipStartDate &&
      format(formData.relationshipStartDate, API_DATE_FORMAT);

    const relationEndDateString =
      formData.relationshipEndDate &&
      format(formData.relationshipEndDate, API_DATE_FORMAT);

    const relationLabel =
      formData.relationLabel?.value ?? formData.relationLabel;
    const relationStatus = formData.relationStatus?.value;

    setIsBusy(true);
    setApiError(null);

    try {
      await updateRelationship(relation.relationshipId, {
        relationStartDateString,
        relationEndDateString,
        relationStatus,
        relationLabel,
      });

      onClose();
    } catch (e) {
      setApiError(translate('API_ERROR_UNKNOWN'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <CustomDialog
      dialogHeader={translate(
        isEmployment
          ? 'EDIT_RELATION_DIALOG_TITLE_EMPLOYMENT'
          : 'EDIT_RELATION_DIALOG_TITLE_HOUSEHOLD',
        {
          displayName: constituent.displayName,
        },
      )}
      fullWidth
      maxWidth="lg"
      onClose={onClose}
      open={open}
      showDialogActions={false}
    >
      <FormProvider {...formMethods}>
        <form className="flex flex-col" onSubmit={handleSubmit(submit)}>
          {isEmployment ? (
            <JobRelationshipForm control={control} />
          ) : (
            <HouseholdRelationshipForm control={control} />
          )}
          {apiError && (
            <div className="mt-4">
              <span className="text-md font-light text-error-main">
                {apiError}
              </span>
            </div>
          )}
          <div className="my-3 flex justify-end">
            {isBusy ? (
              <Loader isCentered size={LoaderSizes.LG} />
            ) : (
              <>
                <Button className="mr-3 p-3" type="button" onClick={onClose}>
                  {translate('CANCEL')}
                </Button>
                <Button
                  className="p-3"
                  type="submit"
                  color="primary"
                  variant="contained"
                >
                  {translate('EDIT_RELATION_DIALOG_ACTION_SUBMIT')}
                </Button>
              </>
            )}
          </div>
        </form>
      </FormProvider>
    </CustomDialog>
  );
}

EditRelationDialog.propTypes = {
  parent: DonorType.isRequired,
  relation: DonorRelationType.isRequired,
  onClose: propTypes.func,
  open: propTypes.bool,
};

EditRelationDialog.defaultProps = {
  onClose: () => {},
  open: false,
};

export default EditRelationDialog;
